import { auth } from "salesive-api-axios";

export const STORAGE_KEY_CONVERSATION_ID = "salesive_widget_conversation_id";

export const extractMessages = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.messages)) return payload.data.messages;
    if (Array.isArray(payload?.messages)) return payload.messages;
    return [];
};

export const extractConversations = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.conversations))
        return payload.data.conversations;
    if (Array.isArray(payload?.conversations)) return payload.conversations;
    return [];
};

// In-memory cache of the most recently observed session identity, populated
// from auth.me() / auth.session() responses. We need this because the JWT now
// lives in an httpOnly cookie that JavaScript cannot read directly — the
// localStorage mirror only gets written on a few code paths (e.g. the Google
// OAuth callback in AuthForm.jsx), so for OTP-based sign-in we rely on
// whatever the most recent auth call returned.
let cachedIdentity = null;
export const setSessionIdentity = (identity) => {
    cachedIdentity = identity || null;
};
export const getCachedIdentity = () => cachedIdentity;

const decodeJwt = (token) => {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        "=",
    );
    return JSON.parse(atob(padded));
};

const getSessionIdentity = () => {
    try {
        // 1) In-memory cache populated from auth.me() / auth.session().
        if (cachedIdentity?.id || cachedIdentity?.email) return cachedIdentity;

        // 2) localStorage mirror — kept on a few write paths for synchronous
        //    consumers (socket handshake, etc.). Once the cookie is the
        //    only source of truth, this branch becomes a no-op for OTP
        //    sign-in (the SDK no longer writes the token there) but is
        //    still useful for the Google OAuth redirect flow.
        if (typeof localStorage === "undefined") return null;
        const token = localStorage.getItem("SALESIVE_ACCESS_TOKEN");
        if (token) {
            const payload = decodeJwt(token);
            if (payload) {
                return {
                    id:
                        payload?.sub ||
                        payload?._id ||
                        payload?.id ||
                        payload?.userId ||
                        payload?.customerId,
                    email: payload?.email,
                };
            }
        }
    } catch (err) {
        return null;
    }
    return null;
};

const SHOPPER_ROLES = new Set(["user", "ghost", "customer", "buyer"]);
const STAFF_ROLES = new Set([
    "admin",
    "seller",
    "staff",
    "owner",
    "agent",
    "ai",
]);

const normalizeRole = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim().toLowerCase();
};

/**
 * Is this event/message from the shopper (right side), vs staff/AI (left)?
 *
 * Prefer explicit roles from the events API (`user` / `ghost` = shopper,
 * `admin` / `seller` / … = staff). Identity matching is a fallback for
 * payloads that omit role — it must not be the only path, because cookie
 * auth often leaves the in-memory identity empty and then the shopper's
 * own typing + messages render as staff.
 */
export const isCustomerMessage = (raw) => {
    if (!raw) return false;

    const sender = raw.sender;
    if (sender === "customer" || sender === "user" || sender === "ghost") {
        return true;
    }

    const senderType = normalizeRole(
        raw.senderType ||
            raw.senderRole ||
            raw.senderKind ||
            (sender && typeof sender === "object"
                ? sender.type || sender.role || sender.kind || sender.shopRole
                : null),
    );

    if (senderType) {
        if (SHOPPER_ROLES.has(senderType) || senderType.includes("customer")) {
            return true;
        }
        if (STAFF_ROLES.has(senderType) || senderType.includes("staff")) {
            return false;
        }
    }

    if (raw.type === "user" || raw.type === "customer") return true;

    if (sender && typeof sender === "object") {
        const identity = getSessionIdentity();
        const senderId = sender._id || sender.id;
        const senderEmail = sender.email;

        if (
            identity?.id &&
            senderId &&
            String(identity.id) === String(senderId)
        ) {
            return true;
        }

        if (
            identity?.email &&
            senderEmail &&
            String(identity.email).toLowerCase() ===
                String(senderEmail).toLowerCase()
        ) {
            return true;
        }
    }

    return false;
};

/** True when a typing payload is from a shopper (including ourselves). */
export const isShopperTypingUser = (user) => {
    if (!user) return false;
    return isCustomerMessage({ sender: user });
};

export const normalizeMessage = (raw) => {
    if (!raw) return null;
    const text = raw.content ?? raw.text ?? "";
    const timestamp = raw.createdAt || raw.timestamp || Date.now();

    const messageType = raw.type; // "text", "image", "file", "system"
    const attachment = raw.attachment || null;

    return {
        id:
            raw.id ||
            raw._id ||
            raw.messageId ||
            raw.clientMessageId ||
            `msg-${Date.now()}-${Math.random()}`,
        clientMessageId: raw.clientMessageId,
        type: isCustomerMessage(raw) ? "user" : "bot",
        text,
        messageType: messageType || "text",
        attachment,
        senderName: raw.sender?.name || raw.shop?.name,
        senderAvatar: raw.sender?.avatar,
        metadata: raw.metadata || null,
        timestamp: new Date(timestamp),
        pending: Boolean(raw.pending),
        read: Boolean(
            raw.read ||
                (Array.isArray(raw.readBy) && raw.readBy.length > 0),
        ),
        readAt: raw.readAt
            ? new Date(raw.readAt)
            : Array.isArray(raw.readBy) && raw.readBy.length > 0
              ? new Date(raw.readBy[0].readAt || raw.readBy[0])
              : null,
    };
};

export const normalizeConversation = (raw) => {
    if (!raw) return null;

    const id = raw.id || raw._id || raw.conversationId;
    if (!id) return null;

    const unreadCount =
        Number(raw.unreadCount ?? raw.unread ?? raw.unreadMessages ?? 0) || 0;

    return {
        ...raw,
        id,
        unreadCount,
    };
};

export const getConversationLabel = (conversation) => {
    if (!conversation) return "Conversation";
    if (conversation.title) return conversation.title;
    if (conversation.subject) return conversation.subject;

    const type = conversation.type;
    if (type === "shop_support") return "Chat Support";
    if (typeof type === "string" && type.trim()) {
        return type.replace(/_/g, " ");
    }

    return "Conversation";
};

export const getMessageConversationId = (raw) => {
    if (!raw) return null;
    if (typeof raw === "string") return raw;

    if (Array.isArray(raw)) {
        if (raw.length === 0) return null;
        return getMessageConversationId(raw[0]);
    }

    if (raw.conversationId) return raw.conversationId;
    if (raw.conversation && typeof raw.conversation === "object") {
        return raw.conversation._id || raw.conversation.id;
    }
    if (typeof raw.conversation === "string") return raw.conversation;

    if (raw.data) {
        const nested = getMessageConversationId(raw.data);
        if (nested) return nested;
    }

    if (raw.messages) {
        const nested = getMessageConversationId(raw.messages);
        if (nested) return nested;
    }

    return raw.threadId || raw.chatId || null;
};

export const getPersistedConversationId = () => {
    try {
        if (typeof localStorage === "undefined") return null;
        return localStorage.getItem(STORAGE_KEY_CONVERSATION_ID);
    } catch (err) {
        return null;
    }
};

export const persistConversationId = (conversationId) => {
    try {
        if (typeof localStorage === "undefined") return;
        if (!conversationId) return;
        localStorage.setItem(
            STORAGE_KEY_CONVERSATION_ID,
            String(conversationId)
        );
    } catch (err) {
        // ignore
    }
};

export const playNotificationSound = () => {
    try {
        const audio = new Audio(
            "https://res.cloudinary.com/computer-geek/video/upload/v1766996105/Salesive/sound/new_u92inp.mp3"
        );
        audio.volume = 0.5; // Set volume to 50% to be safe, or leave default
        const promise = audio.play();

        if (promise !== undefined) {
            promise.catch((error) => {
                // Auto-play was prevented
                console.warn("[SalesiveWidget] Audio play failed", error);
            });
        }
    } catch (err) {
        // ignore errors
        console.warn("[SalesiveWidget] Audio play error", err);
    }
};

export const createClientMessageId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `client-${Date.now()}-${Math.random()}`;
};

export const formatRelativeTime = (dateInput) => {
    if (!dateInput) return "";
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "";

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const inputDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
    );

    if (inputDay.getTime() === today.getTime()) {
        return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
    }

    if (inputDay.getTime() === yesterday.getTime()) {
        return "Yesterday";
    }

    return date.toLocaleDateString([], { month: "short", day: "numeric" });
};
